import { cn } from "@/lib/utils";
import { Avatar } from "@/components/shared/Avatar";
import { SrIcon } from "@/components/ui/SrIcon";
import { TaskComments } from "./TaskComments";
import { BudgetHoursBar } from "./BudgetHoursBar";
import { LoggedEntriesTooltip } from "./LoggedEntriesTooltip";
import { statusFor, STATUS_TEXT } from "@/lib/budget-status";
import type { LoggedEntryRef } from "@/hooks/use-budget-line-logged-hours";

/** Én tildeling slik raden trenger den (delmengde av BudgetLineRow.assignments). */
export interface BudgetAssignmentRowData {
  id: string;
  user_id: string;
  hours: number;
  label?: string | null;
  user?: { full_name: string; avatar_url: string | null } | null;
}

interface Props {
  assignment: BudgetAssignmentRowData;
  /** Loggede timer for denne tildelingen (assignment-presist, med per-bruker-fallback). */
  loggedHours: number;
  /** VIB-1201: føringene bak tallet, til hover/klikk-listen. */
  entries: LoggedEntryRef[] | undefined;
  commentCount: number;
  commentsOpen: boolean;
  onToggleComments: () => void;
  readOnly: boolean;
}

/**
 * Per-person-raden under en budsjettlinje: avatar, navn, kommentar-knapp,
 * tildelte timer, brukte timer og forbruksbar.
 *
 * Skilt ut fra BudgetTab.tsx (VIB-2102) fordi den filen passerte fillengde-
 * grensen da klikk-redigering kom til. Ren presentasjon — all beregning og
 * all rettighetsvurdering skjer hos kallestedet.
 */
export function BudgetAssignmentRow({
  assignment: a,
  loggedHours,
  entries,
  commentCount,
  commentsOpen,
  onToggleComments,
  readOnly,
}: Props) {
  const rounded = Math.round(loggedHours * 10) / 10;
  const status = statusFor(a.hours, loggedHours);
  const hasEntries = !!entries?.length;
  const usedText = rounded > 0 ? `${rounded}t` : "—";

  return (
    <div className="border-b border-border/20 last:border-b-0">
      <div className="grid grid-cols-[24px_1fr_70px_90px_110px] gap-3 items-center px-1 py-2 text-sm">
        <Avatar
          name={a.user?.full_name ?? "?"}
          avatarUrl={a.user?.avatar_url ?? null}
          size="sm"
        />

        <div className="flex items-center gap-2 min-w-0">
          <div className="flex flex-col min-w-0">
            {a.label && <span className="font-medium truncate text-sm">{a.label}</span>}
            <span
              className={cn(
                "truncate",
                a.label ? "text-xs text-muted-foreground" : "font-medium text-sm",
              )}
            >
              {a.user?.full_name ?? "?"}
            </span>
          </div>
          <button
            onClick={onToggleComments}
            className={cn(
              "inline-flex items-center gap-1 text-[11px] hover:text-primary transition-colors",
              commentsOpen && "text-primary",
              commentCount > 0 && !commentsOpen && "text-primary font-medium",
            )}
            aria-expanded={commentsOpen}
          >
            <SrIcon name="message-square" className="w-3 h-3" />
            {commentCount > 0 ? (
              <span className="inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 text-[10px] font-bold rounded-full bg-primary text-primary-foreground tabular-nums">
                {commentCount}
              </span>
            ) : (
              <span>Kommenter</span>
            )}
          </button>
        </div>

        <span className="text-right tabular-nums text-muted-foreground">{a.hours}t</span>

        {/* Brukt — fargemerket per status. VIB-1201: hover/tap viser
            underliggende føringer (dato + fra–til). */}
        <LoggedEntriesTooltip
          userName={a.user?.full_name ?? "?"}
          entries={entries}
        >
          <span
            className={cn(
              "text-right tabular-nums font-semibold",
              STATUS_TEXT[status],
              hasEntries &&
                "underline decoration-dotted decoration-muted-foreground/50 underline-offset-2",
              hasEntries && "cursor-help",
            )}
          >
            {usedText}
          </span>
        </LoggedEntriesTooltip>

        <div>
          {a.hours > 0 ? (
            <BudgetHoursBar assigned={a.hours} used={loggedHours} height={4} />
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </div>
      </div>
      {commentsOpen && (
        <div className="px-1 pb-2">
          <TaskComments assignmentId={a.id} readOnly={readOnly} />
        </div>
      )}
    </div>
  );
}
